import type { UptimeStatusResponse } from '@/types/uptimeStatus';

export type ConnectionStatus = 'connected' | 'disconnected' | 'error';

function formatAge(ms: number | null): string {
  if (ms === null) {
    return 'No data';
  }
  if (ms < 1000) {
    return 'Just now';
  }
  const seconds = Math.round(ms / 1000);
  if (seconds < 60) {
    return `${seconds}s ago`;
  }
  const minutes = Math.floor(seconds / 60);
  const remainingSeconds = seconds % 60;
  return `${minutes}m ${remainingSeconds}s ago`;
}

function formatIsoLocal(iso: string | null): string {
  if (!iso) {
    return 'Never';
  }
  const t = Date.parse(iso);
  if (!Number.isFinite(t)) {
    return iso;
  }
  return new Date(t).toLocaleString();
}

function labelFromStatus(status: ConnectionStatus): string {
  if (status === 'connected') {
    return 'Connected';
  }
  if (status === 'disconnected') {
    return 'Disconnected';
  }
  return 'Connection error';
}

export interface OperatorStatusLocalContext {
  currentStatus: ConnectionStatus;
  lastTabDataUpdateMsAgo: number | null;
  avgTabUpdateIntervalLabel: string;
  recentEventsHtml: string;
}

export function buildOperatorStatusModalHtml(
  snapshot: UptimeStatusResponse,
  local: OperatorStatusLocalContext
): string {
  const hourly = snapshot.scanner_feed_events_by_hour ?? [];
  const maxEv = Math.max(1, ...hourly);
  const bars = hourly
    .map((count, i) => {
      const pct = Math.max(4, Math.round((count / maxEv) * 100));
      return `<div class="status-hourly-bar-wrap" title="Hour slot ${i + 1}/24 (UTC rolling): ${count} event(s)">
        <div class="status-hourly-bar" style="height:${pct}%"></div>
      </div>`;
    })
    .join('');

  const freshnessLabel =
    snapshot.freshness_ms !== null && snapshot.freshness_ms !== undefined
      ? formatAge(snapshot.freshness_ms)
      : 'No mentions in DB';

  return `
    <div class="status-detail-summary">
      <div class="status-detail-summary-card">
        <div class="status-detail-summary-label">Your connection</div>
        <div class="status-detail-summary-value">${labelFromStatus(local.currentStatus)}</div>
      </div>
      <div class="status-detail-summary-card">
        <div class="status-detail-summary-label">Last new mention</div>
        <div class="status-detail-summary-value">${freshnessLabel}</div>
        <div class="text-secondary text-xs mt-1">${formatIsoLocal(snapshot.last_mention_at)}</div>
      </div>
      <div class="status-detail-summary-card">
        <div class="status-detail-summary-label">Scanner</div>
        <div class="status-detail-summary-value">${snapshot.scanner_sessions_connected} session${snapshot.scanner_sessions_connected === 1 ? '' : 's'} active</div>
        <div class="text-secondary text-xs mt-1">Last ping: ${formatIsoLocal(snapshot.last_scanner_data_update_at)}</div>
      </div>
      <div class="status-detail-summary-card">
        <div class="status-detail-summary-label">Uptime (since start / 30 days)</div>
        <div class="status-detail-summary-value">API: ${snapshot.process_uptime_24h_pct.toFixed(1)}% / ${snapshot.process_uptime_30d_pct.toFixed(1)}%</div>
        <div class="text-secondary text-xs mt-1">Scanner feed: ${snapshot.scanner_feed_coverage_24h_pct.toFixed(1)}% / ${snapshot.scanner_feed_coverage_30d_pct.toFixed(1)}%</div>
      </div>
    </div>
    <div class="status-detail-section-label mt-4">Mention activity, last 24 hours (UTC)</div>
    <div class="status-hourly-bars" role="img" aria-label="Scanner events per hour last 24 hours">${bars}</div>
    <div class="text-secondary text-sm mt-3">
      Snapshot taken ${formatIsoLocal(snapshot.generated_at)}
    </div>
  `;
}
